/**
 * Service for handling ActiveStorage direct uploads
 * 
 * This service implements the two-phase upload flow:
 * 1. Request presigned URL from Rails ActiveStorage
 * 2. Upload file directly to S3/MinIO using the presigned URL
 */

import SparkMD5 from 'spark-md5';
import { getServerBaseURL } from '../lib/axios';

interface DirectUploadResponse {
    id: number;
    key: string;
    filename: string;
    content_type: string;
    byte_size: number;
    checksum: string;
    created_at: string;
    signed_id: string;
    direct_upload: {
        url: string;
        headers: Record<string, string>;
    };
}

interface DirectUploadOptions {
    file: File;
    onProgress?: (progress: number) => void;
}

/**
 * Calculate MD5 checksum for file validation
 * Required by ActiveStorage for integrity verification
 * Uses spark-md5 for browser compatibility
 */
async function calculateChecksum(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        const spark = new SparkMD5.ArrayBuffer();

        reader.onload = (e) => {
            if (e.target?.result) {
                spark.append(e.target.result as ArrayBuffer);
                const md5Hash = spark.end();
                // Convert hex to base64 for ActiveStorage
                const hashBase64 = btoa(
                    md5Hash
                        .match(/.{2}/g)!
                        .map((byte) => String.fromCharCode(parseInt(byte, 16)))
                        .join('')
                );
                resolve(hashBase64);
            } else {
                reject(new Error('Failed to read file'));
            }
        };

        reader.onerror = () => {
            reject(new Error('Error reading file for checksum'));
        };

        reader.readAsArrayBuffer(file);
    });
}

/**
 * Request a presigned URL from ActiveStorage
 *
 * @param file - The file to upload
 * @returns Response with presigned URL and metadata
 */
async function requestDirectUpload(
    file: File
): Promise<DirectUploadResponse> {
    // Calculate checksum for file integrity
    const checksum = await calculateChecksum(file);

    // Use centralized server base URL
    const baseURL = getServerBaseURL();
    const url = `${baseURL}/rails/active_storage/direct_uploads`;

    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            blob: {
                filename: file.name,
                content_type: file.type,
                byte_size: file.size,
                checksum: checksum,
            },
        }),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
            errorData.error || `Failed to request direct upload: ${response.statusText}`
        );
    }

    return response.json();
}

/**
 * Upload file directly to storage using presigned URL
 * 
 * @param file - The file to upload
 * @param directUpload - Presigned URL data from ActiveStorage
 * @param onProgress - Optional callback for upload progress
 */
async function uploadToStorage(
    file: File,
    directUpload: DirectUploadResponse['direct_upload'],
    onProgress?: (progress: number) => void
): Promise<void> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        if (onProgress) {
            xhr.upload.addEventListener('progress', (event) => {
                if (event.lengthComputable) {
                    const progress = Math.round((event.loaded / event.total) * 100);
                    onProgress(progress);
                }
            });
        }

        xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
            } else {
                reject(new Error(`Upload failed with status ${xhr.status}`));
            }
        });

        xhr.addEventListener('error', () => {
            reject(new Error('Network error during upload'));
        });

        xhr.addEventListener('abort', () => {
            reject(new Error('Upload was aborted'));
        });

        xhr.open('PUT', directUpload.url);

        // Set headers from presigned URL response
        Object.entries(directUpload.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value);
        });

        xhr.send(file);
    });
}

/**
 * Main function: Complete direct upload flow
 *
 * This orchestrates the full upload process:
 * 1. Request presigned URL from Rails
 * 2. Upload file directly to storage
 * 3. Return signed blob ID for backend attachment
 *
 * @param options - Upload options including file and progress callback
 * @returns Signed blob ID to be used for attachment
 */
export async function directUpload({
    file,
    onProgress,
}: DirectUploadOptions): Promise<string> {
    // Phase 1: Request presigned URL
    onProgress?.(0);
    const directUploadData = await requestDirectUpload(file);

    // Phase 2: Upload to storage (this is where progress tracking happens)
    await uploadToStorage(file, directUploadData.direct_upload, onProgress);

    // Return signed blob ID for backend attachment
    return directUploadData.signed_id;
}
